import type { AppBrowser } from "../app/AppBrowser";
import { RuntimeGS } from "../app/RuntimeGS";
import { StateConfigurationGS } from "./StateConfigurationGS";
import { StateConstructor, StateRunModeEnum, StateRunResultEnum } from "./StateContractsGS";
import { StateRunOptionsGS, StateRuntimeGS, StateRuntimeStoreGS } from "./StateRuntimeGS";
import type { StateGS } from "./StateGS";
import { isStatePagedGS } from "./StatePagedContractGS";
import { adoptStateHeadGS, applyStateHeadGS } from "./StateHeadGS";

interface BrowserAdoptionGS {
    readonly layoutName: string;
    readonly pageElement: HTMLElement;
}

/** @internal Browser-only state lifecycle, navigation and SSR DOM adoption. */
export class StateBrowserRuntimeGS extends StateRuntimeGS {
    private static readonly _documentRuntimes = new WeakMap<Document, StateBrowserRuntimeGS>();
    private static readonly _listeningDocuments = new WeakSet<Document>();
    private static readonly _windowRuntimes = new WeakMap<Window, StateBrowserRuntimeGS>();
    private static readonly _listeningWindows = new WeakSet<Window>();
    private static readonly _nativeLinkAttribute = "gs-link-native";

    private _transitionId = 0;
    private _adoptionAttempted = false;
    /** Preparation may overlap; only the irreversible browser commit is serialized. */
    private _commitTail: Promise<void> = Promise.resolve();

    public constructor(
        app: AppBrowser,
        configuration: StateConfigurationGS
    ) {
        super(app, configuration);
        const runtimeWindow = RuntimeGS.window;
        if (!runtimeWindow) throw new Error("Browser state runtime requires a browser window.");
        StateBrowserRuntimeGS.attachNavigation(runtimeWindow, this);
    }

    private get browserApp(): AppBrowser { return this.app as AppBrowser; }

    public async run(
        constructor: StateConstructor,
        args?: any,
        options: StateRunOptionsGS = {}
    ): Promise<StateRunResultEnum> {
        try {
            this.ensureTransitionAllowed(constructor.StateName);
            const initialUrlActivation = options.fromUrl === true && !this._adoptionAttempted;
            const adoption = options.fromUrl ? this.tryAdoptServerDom(constructor.StateName) : undefined;
            const scope = RuntimeGS.current;
            if (!scope || scope.app !== this.app)
                throw new Error("Browser state activation requires the owning application scope.");

            const state = this.createState(constructor, adoption?.pageElement);
            state.initializeFrameworkState(constructor.StateName);

            if (adoption) {
                if (isStatePagedGS(state) && state.layoutName.toLowerCase() === adoption.layoutName.toLowerCase()) {
                    adoptStateHeadGS(RuntimeGS.document, state);
                    return this.runStateInstanceMode(
                        state,
                        args,
                        { ...options, withHistory: false },
                        StateRunModeEnum.BrowserFromServer
                    );
                }

                console.warn(`GS hydration mismatch for state '${constructor.StateName}'; using a fresh browser render.`);
                this.browserApp.gui.forgetAdoptedLayout(adoption.layoutName);
                const freshState = this.createState(constructor);
                freshState.initializeFrameworkState(constructor.StateName);
                return this.runStateInstanceMode(
                    freshState,
                    args,
                    initialUrlActivation ? { ...options, withHistory: false } : options,
                    StateRunModeEnum.BrowserTransition
                );
            }

            return this.runStateInstanceMode(
                state,
                args,
                initialUrlActivation ? { ...options, withHistory: false } : options,
                StateRunModeEnum.BrowserTransition
            );
        } catch (error) {
            console.error(error);
            return StateRunResultEnum.Failed;
        }
    }

    public runInstance(
        state: StateGS,
        args?: any,
        options: StateRunOptionsGS = {}
    ): Promise<StateRunResultEnum> {
        const stateName = ((state.constructor as StateConstructor).StateName || state.name || "").trim();
        try {
            this.ensureTransitionAllowed(stateName);
        } catch (error) {
            console.error(error);
            return Promise.resolve(StateRunResultEnum.Failed);
        }
        if (!state.name) {
            if (!stateName) throw new Error("StateName must not be empty.");
            state.initializeFrameworkState(stateName);
        }
        return this.runStateInstanceMode(state, args, options, StateRunModeEnum.BrowserTransition);
    }

    public updateStateUrl(state: StateGS): void {
        if (!state.inHistory) throw new Error("Can't update url. State not in history.");
        this.requireRegisteredRoute(state);
        const runtimeWindow = RuntimeGS.window;
        if (!runtimeWindow) throw new Error("History API is unavailable.");
        const status: Record<string, string> = {};
        status[this.urlStateArgName] = state.name;
        runtimeWindow.history.pushState(status, state.title, state.getUrlArgs());
    }

    private handleDocumentClick(event: MouseEvent): void {
        const runtimeWindow = RuntimeGS.window;
        if (!runtimeWindow || event.defaultPrevented || event.button !== 0
            || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const anchor = StateBrowserRuntimeGS.findAnchor(event);
        const rawHref = anchor?.getAttribute("href")?.trim();
        if (!anchor || !rawHref || anchor.hasAttribute(StateBrowserRuntimeGS._nativeLinkAttribute)
            || anchor.hasAttribute("download")) return;
        if (anchor.target && anchor.target.toLowerCase() !== "_self") return;
        if (anchor.rel.split(/\s+/).some(value => value.toLowerCase() === "external")) return;

        let target: URL;
        let args: Record<string, string>;
        try {
            target = new URL(anchor.href, runtimeWindow.location.href);
            if (target.protocol !== "http:" && target.protocol !== "https:") return;
            if (target.username || target.password || target.origin !== runtimeWindow.location.origin) return;
            if (target.hash.includes(":~:text=")) return;
            if (target.hash
                && target.pathname === runtimeWindow.location.pathname
                && target.search === runtimeWindow.location.search) return;
            if (!this.configuration.UsePathRouting
                && target.pathname !== runtimeWindow.location.pathname) return;
            const parsedArgs = this.configuration.parseUrl(target.href);
            if (!parsedArgs) return;
            args = parsedArgs;
        } catch {
            return;
        }

        const stateName = this.configuration.getStateName(args);
        if (!this.configuration.isStateRegistered(stateName)) return;

        event.preventDefault();
        void this.runByUrl(target.href);
    }

    private async runStateInstanceMode(
        state: StateGS,
        args: any,
        options: StateRunOptionsGS,
        mode: StateRunModeEnum
    ): Promise<StateRunResultEnum> {
        const transitionId = ++this._transitionId;

        try {
            let result = await state.preStart(mode, args);
            if (result !== StateRunResultEnum.Activated) return result;
            if (transitionId !== this._transitionId) return StateRunResultEnum.Skipped;

            result = await state.start(args);
            if (result !== StateRunResultEnum.Activated) return result;
            if (transitionId !== this._transitionId) return StateRunResultEnum.Skipped;

            return await this.commitPreparedState(transitionId, state, args, options, mode);
        } catch (error) {
            console.error(error);
            return StateRunResultEnum.Failed;
        }
    }

    private async commitPreparedState(
        transitionId: number,
        state: StateGS,
        args: any,
        options: StateRunOptionsGS,
        mode: StateRunModeEnum
    ): Promise<StateRunResultEnum> {
        const previousCommit = this._commitTail;
        let releaseCommit: () => void = () => undefined;
        this._commitTail = new Promise<void>(resolve => { releaseCommit = resolve; });
        await previousCommit;

        try {
            if (transitionId !== this._transitionId) return StateRunResultEnum.Skipped;

            const writeHistory = options.withHistory !== false
                && state.inHistory
                && this.configuration.isStateRegistered(state.name);
            const execution = StateRuntimeStoreGS.execution();
            const previous = execution.activeState;

            if (previous && previous !== state) {
                try {
                    await previous.stop();
                } catch (error) {
                    previous.setFrameworkActive(true);
                    throw error;
                }
                previous.setFrameworkActive(false);
                if (execution.activeState === previous) delete execution.activeState;
            }

            await this.commitState(state, mode);
            execution.activeState = state;
            state.setFrameworkActive(true);
            execution.afterStartState = state;
            try {
                await state.afterStart(mode, args);
            } catch (error) {
                // Placement is irreversible. A post-activation hook failure must not
                // leave the visible page detached from history and document head.
                console.error(error);
            } finally {
                if (execution.afterStartState === state) delete execution.afterStartState;
            }

            this.applyBrowserEffects(state, writeHistory, options.requestedUrl);
            return StateRunResultEnum.Activated;
        } finally {
            releaseCommit();
        }
    }

    private createState(constructor: StateConstructor, pageElement?: HTMLElement): StateGS {
        const scope = RuntimeGS.current;
        if (!scope || scope.app !== this.app)
            throw new Error("Browser state construction requires the owning application scope.");
        if (pageElement) scope.pendingPageElement = pageElement;
        try {
            return constructor.Instance ?? new constructor();
        } finally {
            delete scope.pendingPageElement;
        }
    }

    private async commitState(state: StateGS, mode: StateRunModeEnum): Promise<void> {
        if (!isStatePagedGS(state)) return;
        const layout = await this.browserApp.gui.place(
            state.layoutName,
            state.page,
            mode === StateRunModeEnum.BrowserFromServer
        );
        const registration = this.browserApp.gui.getRegistration(state.layoutName);
        if (registration) this.app.addInst(registration.constructor, layout);
    }

    private tryAdoptServerDom(stateName: string): BrowserAdoptionGS | undefined {
        if (this._adoptionAttempted) return undefined;
        this._adoptionAttempted = true;

        const layoutElement = RuntimeGS.document.querySelector('[gs-layout]') as HTMLElement | null;
        if (!layoutElement) return undefined;
        const renderedState = layoutElement.getAttribute('gs-state') || '';
        if (renderedState.toLowerCase() !== stateName.toLowerCase()) {
            console.warn(`GS hydration state mismatch: expected '${stateName}', received '${renderedState}'.`);
            layoutElement.remove();
            return undefined;
        }

        const renderedBuild = layoutElement.getAttribute('gs-build') || '';
        if (this.app.buildId && renderedBuild !== this.app.buildId) {
            console.warn(`GS hydration build mismatch: expected '${this.app.buildId}', received '${renderedBuild}'.`);
            layoutElement.remove();
            return undefined;
        }

        const layoutName = layoutElement.getAttribute('gs-layout') || '';
        if (!layoutName || !this.browserApp.gui.getRegistration(layoutName)) {
            console.warn(`GS hydration layout '${layoutName}' is not registered.`);
            layoutElement.remove();
            return undefined;
        }
        try {
            const layout = this.browserApp.gui.adoptLayout(layoutName, layoutElement);
            const pageElement = layout.pageContainer.el.querySelector('[gs-page]') as HTMLElement | null;
            if (!pageElement) {
                this.browserApp.gui.forgetAdoptedLayout(layoutName);
                return undefined;
            }
            return { layoutName, pageElement };
        } catch (error) {
            console.warn(`GS hydration failed for layout '${layoutName}'; using a fresh browser render.`, error);
            layoutElement.remove();
            return undefined;
        }
    }

    private applyBrowserEffects(state: StateGS, withHistory: boolean, requestedUrl?: string): void {
        const runtimeWindow = RuntimeGS.window;
        if (!runtimeWindow) return;
        const fragment = this.getUrlFragment(requestedUrl, runtimeWindow.location.href);
        if (withHistory) {
            const status: Record<string, string> = {};
            status[this.urlStateArgName] = state.name;
            runtimeWindow.history.pushState(
                status,
                state.title,
                this.appendFragment(state.getUrlArgs(), fragment, runtimeWindow.location.href)
            );
        }
        applyStateHeadGS(runtimeWindow.document, state, runtimeWindow.location.href);
        this.scrollToFragment(runtimeWindow, fragment);
    }

    private getUrlFragment(url: string | undefined, baseUrl: string): string {
        if (!url) return "";
        try {
            return new URL(url, baseUrl).hash;
        } catch {
            return "";
        }
    }

    private appendFragment(url: string, fragment: string, baseUrl: string): string {
        if (!fragment) return url;
        const result = new URL(url, baseUrl);
        result.hash = fragment;
        return result.href;
    }

    private scrollToFragment(runtimeWindow: Window, fragment: string): void {
        if (!fragment) {
            runtimeWindow.scrollTo(0, 0);
            return;
        }

        const encodedName = fragment.slice(1);
        let name = encodedName;
        try {
            name = decodeURIComponent(encodedName);
        } catch {
            // Match the literal id/name when a fragment contains invalid percent encoding.
        }

        const target = runtimeWindow.document.getElementById(name)
            ?? runtimeWindow.document.getElementsByName(name)[0];
        target?.scrollIntoView();
    }

    private requireRegisteredRoute(state: StateGS): void {
        if (!this.configuration.isStateRegistered(state.name))
            throw new Error(`State '${state.name}' must be registered before it can write browser history.`);
    }

    private static attachNavigation(runtimeWindow: Window, runtime: StateBrowserRuntimeGS): void {
        const runtimeDocument = runtimeWindow.document;
        StateBrowserRuntimeGS._documentRuntimes.set(runtimeDocument, runtime);
        StateBrowserRuntimeGS._windowRuntimes.set(runtimeWindow, runtime);

        if (!StateBrowserRuntimeGS._listeningDocuments.has(runtimeDocument)) {
            runtimeDocument.addEventListener("click", StateBrowserRuntimeGS.handleDelegatedClick);
            StateBrowserRuntimeGS._listeningDocuments.add(runtimeDocument);
        }
        if (!StateBrowserRuntimeGS._listeningWindows.has(runtimeWindow)) {
            runtimeWindow.addEventListener("popstate", StateBrowserRuntimeGS.handlePopState);
            StateBrowserRuntimeGS._listeningWindows.add(runtimeWindow);
        }
    }

    private static handleDelegatedClick(event: Event): void {
        const runtimeDocument = event.currentTarget as Document | null;
        const runtime = runtimeDocument
            ? StateBrowserRuntimeGS._documentRuntimes.get(runtimeDocument)
            : undefined;
        runtime?.handleDocumentClick(event as MouseEvent);
    }

    private static handlePopState(event: PopStateEvent): void {
        const runtimeWindow = event.currentTarget as Window | null;
        const runtime = runtimeWindow
            ? StateBrowserRuntimeGS._windowRuntimes.get(runtimeWindow)
            : undefined;
        if (runtime && runtimeWindow)
            void runtime.runByUrl(runtimeWindow.location.href, { withHistory: false });
    }

    private static findAnchor(event: MouseEvent): HTMLAnchorElement | undefined {
        for (const target of event.composedPath()) {
            const element = target as Element;
            if (element?.tagName?.toLowerCase() === "a") return element as HTMLAnchorElement;
        }
        const target = event.target as Element | null;
        if (typeof target?.closest !== "function") return undefined;
        return (target.closest("a") as HTMLAnchorElement | null) ?? undefined;
    }
}
